import React, { useState, useRef } from 'react';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Divider,
  Card,
  CardContent,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  Description as FileIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ArrowBack as BackIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import { ReactComponent as UploadBlueIcon } from "../../../../../images/test-automation/uploadIcon_bluecolour.svg";
import { ReactComponent as UploadWhiteIcon } from "../../../../../images/test-automation/uploadIcon_whitecolour.svg";
import { uploadTestDataAPI, getTestDataFilesAPI, deleteTestDataFileAPI } from "../../../../../services/webServices";

const UploadTestDataDrawer = ({ open, onClose, environment, onUploadSuccess, projectId }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templateTabValue, setTemplateTabValue] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const fileInputRef = useRef(null);

  // Log project ID when drawer opens
  React.useEffect(() => {
    if (open && projectId) {
      console.log('🆔 Using session project ID:', projectId);
    }
  }, [open, projectId]);

  // Function to show snackbar notification
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Function to close snackbar
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

        // Sample template data matching Figma design
        const templates = {
          json: {
            name: "JSON Template",
            type: "code",
            content: `{
  "testCases": [
    {
      "testCase": "TC_001",
      "empDeleted": "X253470",
      "amtCheck": "AMT Check",
      "al": "N"
    },
    {
      "testCase": "TC_002",
      "empDeleted": "X253470",
      "amtCheck": "AMT Check",
      "al": "N"
    },
    {
      "testCase": "TC_003",
      "empDeleted": "X253471",
      "amtCheck": "AMT Check",
      "al": "Y"
    }
  ]
}`
          },
          csv: {
            name: "CSV Template",
            type: "code",
            content: `testCase,empDeleted,amtCheck,al
TC_001,X253470,AMT Check,N
TC_002,X253470,AMT Check,N
TC_003,X253471,AMT Check,Y`
          },
          xlsx: {
            name: "XLSX Template",
            type: "table",
            data: [
              { testCase: "TC_001", empDeleted: "X253470", amtCheck: "AMT Check", al: "N" },
              { testCase: "TC_002", empDeleted: "X253470", amtCheck: "AMT Check", al: "N" },
              { testCase: "TC_003", empDeleted: "X253471", amtCheck: "AMT Check", al: "Y" }
            ]
          }
        };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragIn = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragOut = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const allowedTypes = ['.json', '.csv', '.xls', '.xlsx'];
    
    const validFiles = fileArray.filter(file => {
      const extension = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(extension);
    });

    if (validFiles.length !== fileArray.length) {
      showSnackbar('Some files have invalid formats. Only JSON, CSV, XLS, and XLSX files are allowed.', 'error');
    }

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      showSnackbar('Please select at least one file to upload', 'warning');
      return;
    }

    if (!projectId) {
      showSnackbar('No project ID available. Please try again.', 'error');
      return;
    }

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('testDataFiles', file);
      });
      formData.append('environment', JSON.stringify(environment));
      formData.append('projectId', projectId);

      console.log('📤 Uploading with session project ID:', projectId);
      const response = await uploadTestDataAPI(formData);

      if (response.status === 200) {
        const result = response.data;
        showSnackbar(`Successfully uploaded ${result.data.files.length} test data file(s) for project ${projectId}. You can now continue with test file generation.`, 'success');
        setSelectedFiles([]);
        onUploadSuccess && onUploadSuccess(result.data.files, projectId);
        onClose();
      } else {
        showSnackbar(response.data?.message || 'Upload failed', 'error');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showSnackbar('Upload failed. Please try again.', 'error');
    }
  };

  const handleClose = () => {
    setSelectedFiles([]);
    onClose();
  };

  const handleTemplateTabChange = (event, newValue) => {
    setTemplateTabValue(newValue);
  };

  const handleViewTemplates = () => {
    setShowTemplates(true);
  };

  const handleBackFromTemplates = () => {
    setShowTemplates(false);
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
               sx={{
                 '& .MuiDrawer-paper': {
                   width: 550,
                   backgroundColor: '#F6F3F2',
                   padding: 0,
                   boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)'
                 }
               }}
      >
        {/* Compact Header */}
        <Box
          sx={{
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #E0E0E0'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={showTemplates ? handleBackFromTemplates : handleClose}
              size="small"
              sx={{
                color: '#0094FB',
                padding: '4px',
                '&:hover': {
                  backgroundColor: 'rgba(0, 148, 251, 0.04)'
                }
              }}
            >
              <BackIcon />
            </IconButton>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Manrope',
                fontWeight: 600,
                color: '#333',
                fontSize: '18px'
              }}
            >
              {showTemplates ? 'View Templates' : 'Upload test data'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ padding: '4px' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ padding: '20px', flex: 1 }}>
          {!showTemplates ? (
            // Upload Interface
            <>

              {/* Drag and Drop Area */}
              <Box
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  border: dragActive ? "2px solid #0094FB" : "2px dashed #0094FB",
                  borderRadius: "8px",
                  p: 3,
                  textAlign: "center",
                  backgroundColor: dragActive ? "#E1F5FF" : "#F0F8FF",
                  minHeight: "160px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 2,
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  mb: 3,
                  "&:hover": {
                    backgroundColor: "#E1F5FF",
                    borderColor: "#0078CC",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <UploadBlueIcon />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 500, color: "#333", fontFamily: "Manrope" }}
                  >
                    Upload test data
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#666", fontFamily: "Manrope" }}
                >
                  Drag and drop your test data files (.json, .csv, .xls, .xlsx) here, or click to select
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<UploadWhiteIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                  sx={{
                    width: 178,
                    height: 40,
                    backgroundColor: '#0094FB',
                    borderRadius: 2,
                    padding: '8px 16px',
                    fontFamily: 'Manrope',
                    fontWeight: 700,
                    fontSize: 20,
                    lineHeight: '24px',
                    letterSpacing: 0.15,
                    color: 'white',
                    textTransform: 'none',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: '#007ACC',
                    },
                    transition: 'all 0.3s ease-out'
                  }}
                >
                  Choose File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".json,.csv,.xls,.xlsx"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
              </Box>

              {/* View Template Button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button
                  variant="text"
                  onClick={handleViewTemplates}
                  sx={{
                    width: 200,
                    height: 40,
                    backgroundColor: 'transparent',
                    borderRadius: 2,
                    padding: '8px 16px',
                    fontFamily: 'Manrope',
                    fontWeight: 700,
                    fontSize: 20,
                    lineHeight: '24px',
                    letterSpacing: 0.15,
                    color: '#0094FB',
                    textTransform: 'none',
                    border: 'none',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 148, 251, 0.04)',
                    },
                    transition: 'all 0.3s ease-out'
                  }}
                >
                  View Template
                </Button>
              </Box>

                     {/* Uploaded Files */}
                     <Box sx={{ mt: 3 }}>
                       <Typography
                         variant="h6"
                         sx={{
                           fontWeight: 600,
                           color: "#333",
                           mb: 2,
                           fontFamily: "Manrope",
                         }}
                       >
                         Uploaded Files{" "}
                         {selectedFiles.length > 0 && `(${selectedFiles.length})`}
                       </Typography>
                       {selectedFiles.length === 0 ? (
                         <Box
                           sx={{
                             textAlign: "center",
                             py: 4,
                             color: "#666",
                             display: "flex",
                             flexDirection: "column",
                             alignItems: "center",
                             gap: 1,
                           }}
                         >
                           <FileIcon sx={{ fontSize: 48, color: "#ccc" }} />
                           <Typography variant="body2" sx={{ fontFamily: "Manrope" }}>
                             No files uploaded yet
                           </Typography>
                         </Box>
                       ) : (
                         <List>
                           {selectedFiles.map((file, index) => (
                             <React.Fragment key={index}>
                               <ListItem sx={{ px: 0 }}>
                                 <ListItemIcon>
                                   <FileIcon sx={{ color: "#0094FB" }} />
                                 </ListItemIcon>
                                 <ListItemText
                                   primary={file.name}
                                   secondary={`${(file.size / 1024).toFixed(0)} KB • 1 min ago`}
                                 />
                                 <ListItemSecondaryAction>
                                   <IconButton
                                     edge="end"
                                     onClick={() => removeFile(index)}
                                     sx={{ color: "#f44336" }}
                                   >
                                     <DeleteIcon />
                                   </IconButton>
                                 </ListItemSecondaryAction>
                               </ListItem>
                               {index < selectedFiles.length - 1 && <Divider />}
                             </React.Fragment>
                           ))}
                         </List>
                       )}
                     </Box>
            </>
          ) : (
            // Templates Interface - Table Format
            <>
              <Tabs
                value={templateTabValue}
                onChange={handleTemplateTabChange}
                sx={{ 
                  mb: 3,
                  width: '100%',
                  '& .MuiTabs-flexContainer': {
                    justifyContent: 'space-between',
                    width: '100%'
                  },
                  '& .MuiTab-root': {
                    flex: 1,
                    minWidth: 0,
                    maxWidth: 'none'
                  }
                }}
                variant="fullWidth"
              >
                <Tab 
                  label="JSON" 
                  sx={{ 
                    fontFamily: 'Manrope', 
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500
                  }} 
                />
                <Tab 
                  label="CSV" 
                  sx={{ 
                    fontFamily: 'Manrope', 
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500
                  }} 
                />
                <Tab 
                  label="XLSX" 
                  sx={{ 
                    fontFamily: 'Manrope', 
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500
                  }} 
                />
              </Tabs>

              <Typography variant="h6" sx={{ fontFamily: 'Manrope', fontWeight: 600, mb: 2 }}>
                Test data
              </Typography>

              {(() => {
                const currentTemplate = templates[Object.keys(templates)[templateTabValue]];
                
                if (currentTemplate.type === 'code') {
                  // Show code format for JSON and CSV
                  return (
                    <Paper 
                      sx={{ 
                        p: 2, 
                        backgroundColor: '#F8F9FA', 
                        border: '1px solid #E0E0E0',
                        borderRadius: '8px'
                      }}
                    >
                      <pre 
                        style={{ 
                          margin: 0, 
                          fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                          fontSize: '14px',
                          lineHeight: '1.5',
                          color: '#333',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word'
                        }}
                      >
                        {currentTemplate.content}
                      </pre>
                    </Paper>
                  );
                } else {
                  // Show table format for XLSX
                  return (
                    <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #E0E0E0' }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#F8F9FA' }}>
                            <TableCell sx={{ fontFamily: 'Manrope', fontWeight: 600 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Test Case
                                <ArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', fontWeight: 600 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                Emp Deleted EVA Db
                                <ArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', fontWeight: 600 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                AMT Check
                                <ArrowDownIcon sx={{ fontSize: 16, color: '#666' }} />
                              </Box>
                            </TableCell>
                            <TableCell sx={{ fontFamily: 'Manrope', fontWeight: 600 }}>
                              Al
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {currentTemplate.data.map((row, index) => (
                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                              <TableCell sx={{ fontFamily: 'Manrope', color: '#0094FB', fontWeight: 500 }}>
                                {row.testCase}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Manrope' }}>
                                {row.empDeleted}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Manrope' }}>
                                {row.amtCheck}
                              </TableCell>
                              <TableCell sx={{ fontFamily: 'Manrope' }}>
                                {row.al}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  );
                }
              })()}
            </>
          )}
        </Box>

        {/* Compact Footer */}
        <Box
          sx={{
            padding: '16px 24px',
            borderTop: '1px solid #E0E0E0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          {showTemplates ? (
            // Templates Footer
            <>
              <Button
                onClick={handleBackFromTemplates}
                variant="outlined"
                sx={{
                  width: 120,
                  height: 40,
                  border: '2px solid #0094FB',
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                  padding: '8px 16px',
                  fontFamily: 'Manrope',
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: '24px',
                  letterSpacing: 0.15,
                  color: '#0094FB',
                  textTransform: 'none',
                  '&:hover': {
                    border: '2px solid #0094FB',
                    backgroundColor: 'rgba(0, 148, 251, 0.04)',
                  },
                  transition: 'all 0.3s ease-out'
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleBackFromTemplates}
                variant="contained"
                sx={{
                  width: 120,
                  height: 40,
                  backgroundColor: '#0094FB',
                  borderRadius: 2,
                  padding: '8px 16px',
                  fontFamily: 'Manrope',
                  fontWeight: 700,
                  fontSize: 20,
                  lineHeight: '24px',
                  letterSpacing: 0.15,
                  color: 'white',
                  textTransform: 'none',
                  border: 'none',
                  '&:hover': {
                    backgroundColor: '#007ACC',
                  },
                  transition: 'all 0.3s ease-out'
                }}
              >
                OK
              </Button>
            </>
                 ) : (
                   // Upload Footer
                   <>
                     <Button
                       onClick={handleClose}
                       variant="outlined"
                       sx={{
                        width: 120,
                        height: 40,
                        border: '2px solid #0094FB',
                        borderRadius: 2,
                        backgroundColor: 'transparent',
                        padding: '8px 16px',
                        fontFamily: 'Manrope',
                        fontWeight: 700,
                        fontSize: 20,
                        lineHeight: '24px',
                        letterSpacing: 0.15,
                        color: '#0094FB',
                        textTransform: 'none',
                        '&:hover': {
                          border: '2px solid #0094FB',
                          backgroundColor: 'rgba(0, 148, 251, 0.04)',
                        },
                        transition: 'all 0.3s ease-out'
                       }}
                     >
                       OK
                     </Button>
                     <Button
                       onClick={handleUpload}
                       variant="contained"
                       disabled={selectedFiles.length === 0}
                       sx={{
                         width: 120,
                         height: 40,
                         backgroundColor: selectedFiles.length === 0 ? "#ccc" : "#0094FB",
                         borderRadius: 2,
                         padding: '8px 16px',
                         fontFamily: 'Manrope',
                         fontWeight: 700,
                         fontSize: 20,
                         lineHeight: '24px',
                         letterSpacing: 0.15,
                         color: selectedFiles.length === 0 ? "#999" : "white",
                         textTransform: 'none',
                         border: 'none',
                         '&:hover': {
                           backgroundColor: selectedFiles.length === 0 ? "#ccc" : "#007ACC",
                         },
                         transition: 'all 0.3s ease-out'
                       }}
                     >
                       Continue
                     </Button>
                   </>
                 )}
        </Box>
      </Drawer>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            fontFamily: 'Manrope',
            fontSize: '14px'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadTestDataDrawer;
